import React from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const TermsView = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Engelska per default, endast svenska om språket uttryckligen är satt till 'sv'
  const isSv = language === 'sv';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 p-6 md:p-12">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
        
        {/* Tillbaka-knapp */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          {t(language, 'common_back') || (isSv ? 'Tillbaka' : 'Back')}
        </button>

        {/* Rubrik */}
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="text-indigo-600 dark:text-indigo-400" size={32} />
          <h1 className="text-2xl md:text-3xl font-bold">
            {isSv ? 'Användarvillkor' : 'Terms of Service'}
          </h1>
        </div>

        <p className="text-sm text-slate-500 mb-6">
          {isSv ? 'Senast uppdaterad: Oktober 2026' : 'Last updated: October 2026'}
        </p>

        {/* Innehåll */}
        <div className="space-y-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          
          {/* Sektion 1 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '1. Allmänt och Tjänsten' : '1. General & The Service'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Science Monitor tillhandahåller ett automatiserat verktyg för forskningsbevakning och AI-genererade sammanfattningar av vetenskapliga publikationer. Genom att skapa ett konto eller starta en prenumeration godkänner du dessa villkor.
                </>
              ) : (
                <>
                  Science Monitor provides an automated research monitoring platform and AI-synthesized summaries of peer-reviewed scientific publications. By creating an account or subscribing to the service, you agree to these terms.
                </>
              )}
            </p>
          </section>

          {/* Sektion 2 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '2. Prenumeration och Betalning' : '2. Subscriptions & Billing'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Basic-abonnemanget kostar <strong>25 SEK per månad</strong> (inklusive moms). Betalning dras månadsvis i förskott via vår betalningspartner Stripe. Prenumerationen förnyas automatiskt varje månad tills du väljer att säga upp den.
                </>
              ) : (
                <>
                  The Basic plan is billed at <strong>25 SEK per month</strong> (including VAT/sales tax where applicable). Billing is processed monthly in advance via Stripe. Subscriptions renew automatically every month until cancelled.
                </>
              )}
            </p>
          </section>

          {/* Sektion 3 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '3. Begränsningar och Kvoter' : '3. Usage Limits & Quotas'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Basic-prenumerationen inkluderar upp till 5 aktiva forskningsbevakningar och maximalt 25 genererade sammanfattningar per dygn (totalt upp till 775 sammanfattningar per månad). Tjänsten förbehåller sig rätten att begränsa överdrivet eller automatiserat missbruk.
                </>
              ) : (
                <>
                  The Basic subscription includes up to 5 active research watches and a daily synthesis quota (up to 775 summaries per month). The service reserves the right to restrict excessive or automated abuse to safeguard server capacity and stability.
                </>
              )}
            </p>
          </section>

          {/* Sektion 4 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '4. Uppsägning och Ångerrätt' : '4. Cancellation & Right of Withdrawal'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Du kan när som helst säga upp din prenumeration via "Hantera prenumeration" (Stripe Customer Portal) inuti applikationen. Vid uppsägning avslutas debiteringsperioden vid nästa faktureringsdatum, och du behåller full tillgång fram till dess. Eftersom leverans av digitalt innehåll påbörjas omedelbart vid köp samtycker du till att den lagstadgade ångerrätten upphör när tjänsten tas i bruk.
                </>
              ) : (
                <>
                  You may cancel your subscription at any time via the "Manage Subscription" portal in your settings. Upon cancellation, your subscription remains active until the end of the current billing cycle. Because access to digital content and AI services is granted immediately upon purchase, you consent to the statutory right of withdrawal concluding when the service is activated.
                </>
              )}
            </p>
          </section>

          {/* Sektion 5 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '5. Ansvarsfriskrivning för AI-innehåll' : '5. AI Content Disclaimer'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Sammanfattningarna genereras av artificiell intelligens baserat på öppna forskningsdatabaser (OpenAlex). Science Monitor garanterar inte att sammanfattningarna är fullständiga, felfria eller ersätter granskning av originalforskningen och klinisk bedömning.
                </>
              ) : (
                <>
                  Summaries are generated by artificial intelligence based on open scientific repositories (OpenAlex). While designed for factual precision, Science Monitor does not warrant that AI summaries are completely exhaustive, error-free, or a replacement for reviewing original peer-reviewed literature and clinical judgment.
                </>
              )}
            </p>
          </section>

          {/* Sektion 6 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '6. Kontakt' : '6. Contact & Support'}
            </h2>
            <p>
              {isSv ? (
                <>
                  För support, fakturafrågor eller avtalsfrågor kontaktar du oss på:{' '}
                  <a href="mailto:fredrik.mokvist@gmail.com" className="text-indigo-600 dark:text-indigo-400 underline">
                    fredrik.mokvist@gmail.com
                  </a>
                </>
              ) : (
                <>
                  For inquiries, billing support, or terms clarification, reach out to us at:{' '}
                  <a href="mailto:fredrik.mokvist@gmail.com" className="text-indigo-600 dark:text-indigo-400 underline">
                    fredrik.mokvist@gmail.com
                  </a>
                </>
              )}
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsView;